import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import Upload from "./pages/Upload";
import Report from "./pages/Report";
import AuthCallback from "./pages/AuthCallback";
import Auth from "./pages/Auth";
import Profile from "./pages/Profile";
import Pipeline from "./pages/Pipeline";
import Contacts from "./pages/Contacts";
import Practice from "./pages/Practice";
import RoleplaySession from "./pages/RoleplaySession";
import RoleplayResults from "./pages/RoleplayResults";
import VoiceRoleplay from "./pages/VoiceRoleplay";
import Leaderboard from "./pages/Leaderboard";
import Team from "./pages/Team";
import Pricing from "./pages/Pricing";
import Advisor from "./pages/Advisor";
import DealView from "./pages/DealView";
import ScriptBuilder from "./pages/ScriptBuilder";
import Tasks from "./pages/Tasks";
import InviteLanding from "./pages/InviteLanding";
import Contact from "./pages/Contact";
import Features from "./pages/Features";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Admin from "./pages/Admin";
import CookieConsent from "./components/CookieConsent";

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="cyber-spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
}

// Manager-only route - redirects sales reps to dashboard
function ManagerRoute({ children }) {
  const { isAuthenticated, isManager, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="cyber-spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (!isManager) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// App Router - handles session_id detection before routes
function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/upload" 
        element={
          <ProtectedRoute>
            <Upload />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/report/:callId" 
        element={
          <ProtectedRoute>
            <Report />
          </ProtectedRoute>
        } 
      />
      <Route path="/auth" element={<Auth />} />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/pipeline" 
        element={
          <ProtectedRoute>
            <Pipeline />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/contacts" 
        element={
          <ProtectedRoute>
            <Contacts />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/practice" 
        element={
          <ProtectedRoute>
            <Practice />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/practice/session/:sessionId" 
        element={
          <ProtectedRoute>
            <RoleplaySession />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/practice/voice/:sessionId" 
        element={
          <ProtectedRoute>
            <VoiceRoleplay />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/practice/results/:sessionId" 
        element={
          <ProtectedRoute>
            <RoleplayResults />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/leaderboard" 
        element={
          <ProtectedRoute>
            <Leaderboard />
          </ProtectedRoute>
        } 
      />
      <Route path="/pricing" element={<Pricing />} />
      <Route 
        path="/team" 
        element={
          <ManagerRoute>
            <Team />
          </ManagerRoute>
        } 
      />
      <Route 
        path="/advisor" 
        element={
          <ManagerRoute>
            <Advisor />
          </ManagerRoute>
        } 
      />
      <Route 
        path="/deal/:dealId" 
        element={
          <ProtectedRoute>
            <DealView />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/scripts" 
        element={
          <ProtectedRoute>
            <ScriptBuilder />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/tasks" 
        element={
          <ProtectedRoute>
            <Tasks />
          </ProtectedRoute>
        } 
      />
      <Route path="/invite/:token" element={<InviteLanding />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/features" element={<Features />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Admin />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SubscriptionProvider>
          <AppRouter />
          <CookieConsent />
          <Toaster 
            position="top-right"
            toastOptions={{
              style: {
                background: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.4)',
                color: '#374151',
                boxShadow: '0 4px 30px rgba(0, 0, 0, 0.06)',
              },
            }}
          />
        </SubscriptionProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
